import { LoadNewRouteSuggestion } from '@/javascript/domain/usecases'
import { AccessDeniedError, UnexpectedError, ValidationError } from '@/javascript/domain/errors'
import { RouteSuggestion } from '@/javascript/domain/entities'
import { HttpClient, HttpStatusCode } from '@/javascript/data/protocols/http'
import { RemoteRouteSuggestion } from '@/javascript/data/entities/maps/remote-route-suggestion'
import { ListMapper } from '@/javascript/infra/mappers/mapper'

export class RemoteLoadNewRouteSuggestion implements LoadNewRouteSuggestion {
  constructor(
    private readonly url: string,
    private readonly httpClient: HttpClient<RemoteLoadNewRouteSuggestion.Response>,
    private readonly routeSuggestionMapper: ListMapper<RouteSuggestion, RemoteRouteSuggestion>
  ) {}

  async load (params: LoadNewRouteSuggestion.Params): Promise<LoadNewRouteSuggestion.Result> {
    const httpResponse = await this.httpClient.request({
      url: this.url,
      method: 'post',
      body: JSON.stringify({
        start_time: params.startTime,
        end_time: params.endTime,
        works: params.works.map(work => ({
          id: work.id,
          duration: work.duration
        })),
        account_members: params.members.map(member => ({
          id: member.id,
          work_limit: member.configuration.workLimit,
          coordinates: {
            initial: {
              latitude: member.configuration.initial.latitude,
              longitude: member.configuration.initial.longitude,
              address: member.configuration.initial.address
            },
            final: {
              latitude: member.configuration.final.latitude,
              longitude: member.configuration.final.longitude,
              address: member.configuration.final.address
            }
          }
        }))
      })
    })
    const response = httpResponse.body
    switch (httpResponse.statusCode) {
      case HttpStatusCode.ok:
        return {
          suggestions: this.routeSuggestionMapper.toEntityList(response?.result) ?? [],
          unassignedWorkIds: response?.unassigned_work_ids ?? [],
          unassignedMemberIds: response?.unassigned_member_ids ?? []
        }
      case HttpStatusCode.unprocessableEntity:
        throw new ValidationError({
          message: response.error, reason: response.reason, references: response.references,
          unassignedMemberIds: response.unassigned_member_ids
        })
      case HttpStatusCode.forbidden: throw new AccessDeniedError()
      default: throw new UnexpectedError()
    }
  }
}

export namespace RemoteLoadNewRouteSuggestion {
  export type Response = {
    error?: string
    reason?: string
    references?: number[]
    unassigned_work_ids?: number[]
    unassigned_member_ids?: number[]
    result?: RemoteRouteSuggestion[]
  }
}
